import {useMemo} from "react";

import {PRICING_ROUTES, USER_ROLES, WEBBACK_V2_API} from "@constants";
import {useUserApp} from "@hooks";

const V2_PRICING = `${WEBBACK_V2_API}/pricing`

// **The url no longer depends on the role.** v1 has a twin nested for a retailer
// (`pricing-retailers`) with duplicated logic; v2 serves every endpoint of this screen from
// `pricing-and-discount` and decides from the user's role what the price is compared against — the
// RRSP for a brand, their own main site for a retailer. Sending a role to a `-retailers` url on v2
// would 404 for these routes: they do not exist there.
//
// The seven endpoints behind this hook are all unified: `widgets`, `widgets-evolution`,
// `country-wise`, `candlestick`, `candlestick-details`, `top-price-changes` and
// `top-stock-changes`, plus `evolution`, which is shared with Market Analyzer. Their contracts are
// identical for both roles — only the labels change (Meet/Beat/Lose against Same/Above/Below RRSP)
// and, in two of them, one asymmetric key that v2 always emits.
//
// Reasoning: dipi-webback-v2/plans/869e5pfw2--data-pricing-overview.md §2 (which routes unify and
// why the table does not), and §5.bis / §5.ter for the two data gaps still declared against the
// retailer variant.
//
// ⚠️ **Two endpoints are the exception, and both keep their own base url per role**, because v2 kept
// v1's split where the contract really differs (`app/domains/pricing/pricing_retailers/`):
//
// - **`market-comparison`** (+ its Excel) — the Market Dispersions table. It has its own copy of
//   this hook: `@Components/TopMarketDisperions/@hooks/useRoleConfig.js`.
// - **`evolution`** — the SKU drill-down, hence `urlApiEvolution` below. A retailer's chart is *not*
//   the standard one: its series are named `Retailer (product_id)`, it adds one
//   `… (Main Site)` series per market, and it does **not** split 1P from 3P. Sending a retailer to
//   the standard route answers a chart that looks plausible and is wrong — no main site, no product
//   ids and a phantom `Amazon.es (3p)` series. Found on screen with `occiadmin` (2026-08-05).
//
// The MARKETPLACES branch is dropped rather than ported: that role is out of scope and is not
// coming back. It pointed at the same url as STANDARD anyway.

export default function usePricingDiscountRoleConfig() {
	const { userRole } = useUserApp()
	const isRetailerUser = userRole === USER_ROLES.RETAILER

	const typeKpi = useMemo(() => ({
		[USER_ROLES.STANDARD]: 'Discount',
		[USER_ROLES.RETAILER]: 'Dispersion',
	})[userRole], [userRole])

	const { titlePage, subtitlePage } = useMemo(() => ({
		[USER_ROLES.STANDARD]: { titlePage: PRICING_ROUTES.TITLE, subtitlePage: PRICING_ROUTES.SUBTITLE },
		[USER_ROLES.RETAILER]: { titlePage: PRICING_ROUTES.TITLE_RETAILER, subtitlePage: PRICING_ROUTES.SUBTITLE_RETAILER }
	})[userRole], [userRole])

	return {
		userRole,
		urlApi: `${V2_PRICING}/pricing-and-discount`,
		// The SKU drill-down is the one chart of this hook that keeps a base url per role.
		urlApiEvolution: isRetailerUser
			? `${V2_PRICING}/pricing-retailers`
			: `${V2_PRICING}/pricing-and-discount`,
		typeKpi,
		isRetailerUser,
		isStandardUser: userRole === USER_ROLES.STANDARD,
		titlePage,
		subtitlePage
	}
}
